import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { OfferPageActions, OfferPageMultiRoomAutoselectionReason } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import { useContext, useEffect } from 'react'
import { getActivePackagesByRoom, getCalendarsToBeFetchedByRoom, getIsLoadingByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import useOfferPageDispatch from './useOfferPageDispatch'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'

export default function useHotelIndividualRoomAutoselection() {
  const offerPageState = useContext(OfferPageStateContext)
  const { rooms } = offerPageState

  const dispatch = useOfferPageDispatch()
  const isLoadingByRoom = useAppSelector(appState => getIsLoadingByRoom(offerPageState, appState))
  const calendarsToBeFetchedByRoom = useAppSelector(appState => getCalendarsToBeFetchedByRoom(offerPageState, appState))

  const isMultiRoomUIActive = useAppSelector(appState => isMultipleRoomBookingActive(offerPageState, appState))

  const isAnyPackageDateLoading = Object.values(isLoadingByRoom).some(Boolean) || Object.values(calendarsToBeFetchedByRoom).some(Boolean)

  const selectedPackagesByRoom = useAppSelector(appState => getActivePackagesByRoom(offerPageState, appState))

  useEffect(() => {
    if (!isMultiRoomUIActive || isAnyPackageDateLoading) {
      return
    }

    const anySelectionsDiffer = rooms.some(room => {
      const selection = selectedPackagesByRoom[room.id]
      return room.roomId !== selection?.package.id || room.rateId !== selection?.package.roomRate.id
    })

    if (!anySelectionsDiffer) {
      return
    }

    const hasInvalidSelections = rooms.some(room => {
      const hadPreviousSelection = !!room.roomId
      if (!hadPreviousSelection) return false

      const selection = selectedPackagesByRoom[room.id]
      return room.roomId !== selection?.package.id || room.rateId !== selection?.package.roomRate.id
    })

    const autoselectionReason: OfferPageMultiRoomAutoselectionReason = hasInvalidSelections ? 'invalid-selections' : 'initial-selection'

    const newRooms = rooms.map(room => {
      const selection = selectedPackagesByRoom[room.id]
      return {
        ...room,
        roomId: selection?.package.id,
        rateId: selection?.package.roomRate.id,
        uniqueKey: selection?.package.uniqueKey,
      }
    })

    dispatch({ type: OfferPageActions.setRooms, rooms: newRooms })
    dispatch({ type: OfferPageActions.setMultiRoomAutoselectionReason, reason: autoselectionReason })
  }, [dispatch, isMultiRoomUIActive, rooms, selectedPackagesByRoom, isAnyPackageDateLoading])
}
